import { useMutation, useQuery } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { format } from 'date-fns';
import defaultTo from 'lodash/defaultTo';
import pick from 'lodash/fp/pick';

import get from 'lodash/get';
import React, { Fragment, useState } from 'react';
import { compose, withProps } from 'recompose';
import styled from 'styled-components';
import { withScope } from '../../../contexts/ScopeContext';
import SelectionContextProvider from '../../../features/selection/SelectionContextProvider';
import schema, {
  intakeStatistics,
  setIntakeStatus,
  registrationPackagesPaginatedQuery,
  registrationPackagesCountQuery,
  proceduresByIdSubscription,
} from '../../../graph/procedures';
import { procedureForms } from '../../../graph/surgeon/forms';
import intakeProceduresSource from '../../../omnisearch/sources/hospital/intakeProcedures';
import { DefaultTitle } from '@/se/components/collection/CollectionList';
import EntityEmptyState from '@/se/components/entity-states/EntityEmptyState';
import EntityView from '@/se/components/entity/EntityView';

import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import SelectInput from '@/se/components/inputs/SelectInput';
import { withTheme } from '@/se/theme';
import { withSession } from '../../../state/Session';
import { FormType } from '../../../types/Form';
import { ProcedureStatus } from '../../../types/ProcedureEvent';
import { getLastProcedureEvent } from '../../../util/procedureEvents';
import { NamedRange } from '../../core/DatePicker';
import FileUploaderIntake from '../../FileUploaderIntake';
import Filters from '../../pages/analytics/Filters';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import { inTakeFormColumns, inTakeFormListColumns } from './columns';
import ScheduledProcedureMobileListItem from './components/ScheduledProcedureMobileListItem';
import StatChips from './StatChips';
import Forms from './Forms';
import IntakePackageGenerator from './IntakePackageGenerator';
import PatientVisits from './PatientVisits';
import ProcedureEventLog, { filterIntakeEvents, intakeStatusLabels } from './ProcedureEventLog';
import ProcedureInput from './ProcedureInput';
import QuestionnaireAnswers, { QuestionnaireTypeContext } from './QuestionnaireAnswers';
import { a11yProps, TabPanel } from './TabPanel';
import useCareAppAccess from './utils/useCareAppAccess';

const Empty = withProps({
  title: `There are no scheduled procedures at the moment.`,
  hint: '',
  illustration: withTheme(theme => get(theme, 'illustration.patients')),
})(EntityEmptyState);

// Note: tabSwitcher reducer was removed as it was unused

const StatusWrapper = styled.div`
  display: inline-block;
  vertical-align: middle;
  margin-left: 1rem;
  min-width: 16rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StatusSelectInput = withProps({
  placeholder: 'Status',
  isSearchable: false,
  menuWidthFollowContent: true,
  isClearable: true,
  name: 'status',
})(SelectInput);

const Title = ({ data }) => {
  const [updateIntakeStatus] = useMutation(setIntakeStatus);
  const handleIntakeStatusChange = async e => {
    await updateIntakeStatus({
      variables: {
        id: data.id,
        status: e,
      },
    });
  };

  return (
    <DefaultTitle>
      {data ? `#${defaultTo(data.id, '#')} - ${data.patientName ? data.patientName : 'Unknown'}` : '-'}
      <StatusWrapper>
        <Box mr={2}>
          <StatusSelectInput
            onChange={handleIntakeStatusChange}
            value={data?.intakeStatus}
            options={intakeStatusLabels}
            isClearable={true}
          />
        </Box>
      </StatusWrapper>
    </DefaultTitle>
  );
};

const ProcedureAndInTakeForm = props => {
  const { scope, accessToken, data } = props;
  useCareAppAccess(get(scope, 'hospital.id'), accessToken);
  const procedureId = data?.id;
  const lastPreOpCompletedEvent = getLastProcedureEvent(data?.events || [], ProcedureStatus.PreOpCompleted);

  const { data: queryData } = useQuery(procedureForms, {
    variables: { procedureId, formType: FormType.InTakeForm },
  });
  const forms = queryData?.procedureForms || [];

  const formsExists = queryData?.procedureForms?.length > 0;

  const [tab, setTab] = useState(null);
  const tabNumber = tab || 0;

  const procedureEvents = (data?.events || []).filter(event => event.type !== 'Note');

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  return (
    <Fragment>
      <EntityView {...props} />
      {procedureId && <FileUploaderIntake procedureId={procedureId} />}
      {formsExists && (
        <Box mt={6} minWidth={0}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr .5fr' }}>
            <Tabs indicatorColor={'primary'} value={tabNumber} onChange={handleChange} aria-label="Form Tabs">
              <Tab label="Registration Package Questionnaire" {...a11yProps(0)} />
              <Tab label="Forms" {...a11yProps(1)} />
              <Tab label="Patient Visits" {...a11yProps(2)} />
              <Tab label="Procedure Events" {...a11yProps(3)} />
            </Tabs>
            {procedureId && <IntakePackageGenerator procedureId={procedureId} />}
          </div>
          <TabPanel value={tabNumber} index={0}>
            <QuestionnaireTypeContext.Provider value="InTake Form">
              <QuestionnaireAnswers
                entryQuestionnaire={get(props, 'data.inTakeForm')}
                procedure={get(props, 'data', {})}
              />
            </QuestionnaireTypeContext.Provider>
          </TabPanel>
          <TabPanel value={tabNumber} index={1}>
            <Forms procedureId={procedureId} forms={forms} lastPreOpCompletedEvent={lastPreOpCompletedEvent} />
          </TabPanel>
          <TabPanel value={tabNumber} index={2}>
            <PatientVisits
              visits={props?.data?.visits}
              currentVisit={props?.data?.id}
              baseUrlProvider={props.baseUrlProvider}
            />
          </TabPanel>
          <TabPanel value={tabNumber} index={3}>
            <ProcedureEventLog filterEvents={filterIntakeEvents} events={procedureEvents} name="Procedure Events" />
          </TabPanel>
        </Box>
      )}
      {!formsExists && (
        <Box mt={6}>
          <QuestionnaireTypeContext.Provider value="InTake Form">
            <QuestionnaireAnswers
              entryQuestionnaire={get(props, 'data.inTakeForm')}
              procedure={get(props, 'data', {})}
            />
          </QuestionnaireTypeContext.Provider>
        </Box>
      )}
    </Fragment>
  );
};

const CustomFilter = withProps({
  textSearchKey: 'name',
  hideProcedureStatusSelectInput: true,
  hideIntakeStatusSelectInput: false,
  hideCategorySelectInput: false, // Show category filter (tabs by default, dropdown with ?categoryUI=dropdown)
  hideSpecialitySelectInput: false,
  hideProcedureTypeSelectInput: true,
  futurePicker: true,
  defaultValue: {
    dateRange: NamedRange.nextSevenDays(),
  },
})(Filters);

/**
 * Stat items configuration for the header chips.
 */
const intakeStatItems = [
  {
    title: 'Scheduled',
    getter: data => get(data, 'intakeStatistics.scheduledProcedures', '-'),
  },
  {
    title: 'Submitted',
    getter: data => get(data, 'intakeStatistics.totalForms', '-'),
  },
];

/**
 * HeaderLeft render function for stat chips.
 * Stats are now displayed in the list header row alongside Print/Config icons.
 */
const IntakeHeaderLeft = ({ filter }) => (
  <StatChips subscription={intakeStatistics} items={intakeStatItems} filter={filter} />
);

const InTakeFormsCRUD = GraphQLCRUD({
  entityName: 'RegistrationPackage',
  scheme: { ...schema, list: schema.inTakeFormsSubscription, item: schema.inTakeForm },
  List: {
    tableKey: 'RegistrationPackage',
    MobileItemComponent: ScheduledProcedureMobileListItem,
    useColumnSelection: true,
    columns: inTakeFormListColumns,
    // All columns visible by default - users can hide via Configure Columns
    // Column order is optimized for ASC nurse workflow in columns.tsx
    Empty,
    FilterComponent: CustomFilter,
    HeaderLeft: IntakeHeaderLeft,
    defaultFilterValues: {
      dateRange: NamedRange.nextSevenDays(),
    },
    defaultSort: { title: 'Date of Service', type: 'ASC' },
    pickFilter: pick([
      'name',
      'physician',
      'procedureType',
      'hospital',
      'intakeStatus',
      'dateRange',
      'category',
      'speciality',
      'view',
    ]),
    // Enable server-side pagination with IDs-based subscription
    // Query for initial/paginated data, Subscription for real-time updates (only current page IDs)
    paginationConfig: {
      query: registrationPackagesPaginatedQuery,
      countQuery: registrationPackagesCountQuery,

      // IDs-based subscription - only subscribes to items on current page
      // Much more efficient than full-list subscription for paginated views
      subscription: proceduresByIdSubscription,
      subscriptionKey: 'proceduresById',

      entityKey: 'registrationPackagesPaginated',
      countKey: 'registrationPackagesCount',
      // pageSize defaults to 50 via DEFAULT_PAGE_SIZE
    },
  },
  Show: {
    columns: inTakeFormColumns,
    View: compose(withSession(unpackSessionObject), withScope)(ProcedureAndInTakeForm),
    Empty,
    Title,
  },
  Edit: {
    Input: ProcedureInput,
    skipRefetch: true,
    prepareUpdateData: ({ patientDateOfBirth, ...rest }) => {
      console.log('rest1234', rest);
      return {
        ...rest,
        patientDateOfBirth: format(patientDateOfBirth, 'YYYY-MM-DD'),
      };
    },
    mapEditItemProps: ({ data }) => ({
      data: {
        ...data,
        scheduledProcedure: {
          ...data?.scheduledProcedure,
          patientName: data?.scheduledProcedure?.patientNameUnformatted,
          patientDateOfBirth: data?.scheduledProcedure?.patientDateOfBirthISO,
        },
      },
    }),
  },
  searchSource: intakeProceduresSource,
});

const InTakeForms = props => (
  <SelectionContextProvider>
    <InTakeFormsCRUD {...props} />
  </SelectionContextProvider>
);

export default InTakeForms;
